<template>
  <CopyTrackWrapper
    :type="savedGoalContentType"
    :title="pageContent.title"
    :uuid="pageContent.uuid"
    :demo="pageContent.demo"
    class="content-page">
    <LibraryRedesignBanner :title="student.name || ''">
      <template v-slot:breadcrumbList>
        <li v-if="student && student.uuid"><a :href="libraryFolder(student.uuid)"><i class="fa fa-folder header-folder" /> {{ student.name }}</a></li>
        <li v-if="initialSavedPage && initialSavedPage.title" class="active">Goal: {{ initialSavedPage.title }}</li>
      </template>
    </LibraryRedesignBanner>
    <NoAccessError v-if="hasAccess === false"/>
    <div v-else class="container">
      <p v-if="isLoading" class="lead text-muted">
        <i class="fa fa-circle-o-notch fa-spin" /> Loading...
      </p>

      <div class="row">
        <div class="col-sm-8">
          <h1>
            {{ initialSavedPage.title }}
          </h1>
          <div class="label-container">
            <span class="label label-info"
            ><i class="fa fa-pencil" aria-hidden="true" />&nbsp; Library Goal</span
            >&nbsp;
            <span class="label label-success"
            ><i class="fa fa-user" /> Saved to
              <span class="fs-block">{{ student.name }}</span></span
            >
          </div>

          <BigGoal
            :footer="
              pageContent.goal_example_text && pageContent.goal_example_text.length > 0
                ? true
                : false
            "
            :header="goalHeaderText"
            :event-context="{ adapted_goal_level: 'grade' }"
          >
            <CopyTrackWrapper
              slot="body"
              :type="savedGoalContentType"
              :title="pageContent.title"
              :level="'Grade Level'"
              :uuid="pageContent.uuid"
              :demo="pageContent.demo"
            >
              <template v-if="!accessInfo.blur">
                <EditableWikiTextArea
                  v-model="editableText.goalText"
                  :strategies="strategyMap"
                  v-if="editableText.goalText"
                  @textEdited="gradeGoalEdited()"
                  @textReset="
                    resetGoalTextForLevel({ level: 'grade', text: pageContent.goal_text })
                  "
                />
              </template>
              <template v-if="accessInfo.blur">
                <WikiText
                  :text="pageContent.goal_text"
                  :strategies="strategyMap"
                  :locked="accessInfo.blur"
                  lock-mode="always_snippet_first_block"
                  :plaintext="accessInfo.blur"
                />
              </template>
            </CopyTrackWrapper>

            <template v-slot:footer v-if="pageContent.goal_example_text">
              <GoalExampleContent @toggle="clickedExampleContent">
                <CopyTrackWrapper
                  type="goal-example"
                  :title="pageContent.title"
                  :uuid="pageContent.uuid"
                  :demo="pageContent.demo"
                >
                  <WikiText :text="pageContent.goal_example_text" :locked="accessInfo.blur" />
                </CopyTrackWrapper>
              </GoalExampleContent>
            </template>
          </BigGoal>

          <div class="row" v-if="accessInfo.blur">
            <div class="col-sm-10 col-sm-offset-1">
              <limit-access-alert :access-info="accessInfo" />
            </div>
          </div>

          <AdaptedGoalsAccordion
            v-if="adaptedGoalsArray"
            :goal-uuid="initialSavedPage.uuid"
            :goal-title="initialSavedPage.title"
            :subject="initialSavedPage.subject"
            :adapted-goals="editableText.adaptedGoals"
            :strategies="strategyMap"
            :access-info="accessInfo"
            :original-goals="pageContent.objectives"
            @adaptedGoal:edited="adaptedGoalTextEdited"
            @adaptedGoal:reset="resetGoalTextForLevel"
            @adaptedGoal:paneltoggle="trackAdaptedGoalClick"
          />

          <h2>UDL-Aligned Strategies <small><a
            :href="aboutUDLLink"
            target="_blank"
            title="About Universal Design for Learning">About UDL <OpensInNewTabIcon :external-link="true" /></a></small></h2>
          <UdlAdaptationPanelGroup
            :udl-sections="udlAdaptations"
            :locked="accessInfo.blur"
            :strategies="strategyMap"
          />
        </div>
        <div class="col-sm-4">
          <LibraryContentPanel
            :content-type="CONTENT_TYPES.SAVED_GOAL"
            :content-save-status="contentSaveStatus"
            :content-last-save="initialSavedPage.updated_at | formatTimeAgo"
            :source-content-uuid="initialSavedPage.goal_uuid"
            :copy-content-props="{
              personalized: unsavedChanges,
              gradeLevelGoal: editableText.goalText,
              adaptedGoals: editableText.adaptedGoals,
              goalUuid: initialSavedPage.goal_uuid,
              originalStudent: student,
              anchorPage: false
            }"
            @save="saveChanges"
            @discard="discardChanges"
            @print="togglePrintView(true)"
          />

          <ShareGoalPanel
            :uuid="uuid"
            :source-uuid="initialSavedPage.goal_uuid"
            v-if="initialSavedPage.goal_uuid"
            :anchor-page="false"
          />

          <ProgressMonitoringPanel
            v-if="pageContent.title && pageContent.uuid"
            :content-title="pageContent.title"
            :content-uuid="pageContent.uuid"
            :content-type="CONTENT_TYPES.SAVED_GOAL"
          />

          <template v-if="fetchingStandards || fetchingStaircase">
            <p class="text-muted"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</p>
          </template>
          <GoalContentPageStandards
            :show-edit-link="editorVisibility"
            :content-profile-id="profileId"
            :grouped-standards="visibleGroupedStandardsBySetCode"
          />

          <template v-if="staircaseContent && staircaseContent.length > 0">
            <h2>Standard Staircase</h2>
            <CopyTrackWrapper
              type="staircase"
              :title="pageContent.title"
              :uuid="pageContent.uuid"
              :demo="pageContent.demo"
            >
              <Staircase
                :page-uuid="pageContent.uuid"
                :grades="pageContent.grades"
                :goal-staircase="staircaseContent"
              />
            </CopyTrackWrapper>
          </template>

          <template v-if="strategies && strategies.length > 0">
            <h2>Referenced Strategies</h2>
            <StrategyMediaList :strategies="strategies" />
          </template>
        </div>
      </div>

      <PrintableSavedGoalPage
        @update:show="togglePrintView($event)"
        @print="onPrint"
        v-if="showPrintablePage"
        :adapted-goals="editableText.adaptedGoals"
        :grade-level-goal="editableText.goalText"
        :student-name="student.name"
        :title="initialSavedPage.title"
        :grouped-standards="visibleGroupedStandardsBySetCode"
        :subject="initialSavedPage.subject"
      />

      <LimitActionModal
        :show.sync="showPrintBlocker"
        :action-permissions="actionPermissions.printSavedGoal"
      />

      <SubmitFeedback
        :title="initialSavedPage.title"
        :uuid="uuid"
        type="Saved Goal Detail Page"
        :subject="initialSavedPage.subject"
      />
    </div>
  </CopyTrackWrapper>
</template>

<script>
import {
  goalPageMixin,
  saveCustomContentWithContentProfileMixin,
  trackGoalContentEditedMixin,
} from '@/components/goals/common/goal_content_mixin'
import { savedGoalContentMixin } from '@/components/goals/common/saved_goal_content_mixin'
import { authorizationCheckMixin } from '@/router/authorization_mixin'
import LibraryBanner from '@/components/library/LibraryBanner'
import LibraryRedesignBanner from '@/components/library/LibraryRedesignBanner'
import AdaptedGoalsAccordion from '@/components/goals/AdaptedGoalsAccordion'
import ProgressMonitoringPanel from '@/components/goals/ProgressMonitoringPanel'
import LibrarySaveChangesButtons from '@/components/goals/LibrarySaveChangesButtons'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import UdlAdaptationPanelGroup from '@/components/goals/UdlAdaptationPanelGroup'
import LimitAccessAlert from '@/components/LimitAccessAlert'
import LimitActionModal from '@/components/LimitActionModal'
import StrategyMediaList from '@/components/strategies/StrategyMediaList'
import ShareGoalPanel from '@/components/goals/ShareGoalPanel'
import PrintableSavedGoalPage from '@/components/goals/PrintableSavedGoalPage'

import { toolkitUrls, goalbookUrls } from '@/links'
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'
import { saveGoalForStudent } from '@/api/library'
import OpensInNewTabIcon from '../OpensInNewTabIcon.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import { CONTENT_SAVE_STATES } from '@/util/contentSaveStates'
import NoAccessError from '@/components/NoAccessError.vue'

export default {
  name: 'SavedGoalContentPage',
  mixins: [
    authorizationCheckMixin,
    goalPageMixin,
    saveCustomContentWithContentProfileMixin,
    savedGoalContentMixin,
    trackGoalContentEditedMixin,
  ],
  components: {
    NoAccessError,
    LibraryContentPanel,
    LibraryBanner,
    LibraryRedesignBanner,
    AdaptedGoalsAccordion,
    EditableWikiTextArea,
    UdlAdaptationPanelGroup,
    LimitAccessAlert,
    StrategyMediaList,
    ProgressMonitoringPanel,
    LibrarySaveChangesButtons,
    ShareGoalPanel,
    PrintableSavedGoalPage,
    LimitActionModal,
    OpensInNewTabIcon,
  },
  props: {
    uuid: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      editableText: {
        goalText: '',
        adaptedGoals: [],
      },
      saving: false,
      justSaved: false,
      saveError: null,
      showPrintablePage: false,
      showPrintBlocker: false,
      CONTENT_TYPES,
      CONTENT_SAVE_STATES,
      aboutUDLLink: goalbookUrls.aboutUDL,
      hasAccess: undefined,
      ...toolkitUrls
    }
  },
  computed: {
    ...mapState('user', { userUuid: (state) => state.user.uuid }),
    ...mapGetters('goaldetail', ['udlAdaptations']),
    ...mapState('goaldetail', ['saveToLibraryStatus', 'strategies']),
    ...mapGetters('customgoal', ['savedGoalPayload', 'levelToAttributeNames']),
    ...mapGetters('visibility', ['editorVisibility']),
    contentSaveStatus() {
      if (this.justSaved) {
        return this.CONTENT_SAVE_STATES.JUST_SAVED
      }
      if (this.saving) {
        return this.CONTENT_SAVE_STATES.SAVING
      }
      if (this.unsavedChanges) {
        return this.CONTENT_SAVE_STATES.UNSAVED
      }
      return this.CONTENT_SAVE_STATES.SAVED
    },
    savedGoalPayloadWithContentProfile() {
      return this.contentPayloadWithContentProfile(this.savedGoalPayload)
    },
    editGoalUrl() {
      return toolkitUrls.editorLinks.editGoal(this.pageContent.uuid)
    },
    saveButtonText() {
      if (this.saving) {
        return 'Saving...'
      }
      if (this.unsavedChanges) {
        return 'Save Changes'
      }
      if (this.justSaved) {
        return 'Saved'
      }
      return 'No Changes Made'
    },
    unsavedChanges() {
      return (
        this.editableText.goalText !== this.initialSavedPage.grade_level_goal ||
        this.editableText.adaptedGoals.some((goal) => {
          return goal.text !== this.initialSavedPage[this.levelToAttributeNames[goal.level]]
        })
      )
    },
  },
  methods: {
    ...mapActions('contentAccess', ['trackPrint']),
    ...mapActions('library', ['fetchCurrentContentFolder']),
    ...mapMutations('library', ['clearCurrentContentFolder']),
    ...mapMutations('goaldetail', [
      'setSaveToLibrarySuccess',
      'setSaveToLibraryError',
      'resetSaveStatus',
    ]),
    ...mapMutations('customgoal', {
      resetGoalTextForLevel: 'resetGoalText',
    }),
    async loadPageContent() {
      try {
        let savedGoal = await this.fetchLibraryGoal()
        this.hasAccess = true

        this.initializeEditableTextFromSaved()
        setTitle(`${savedGoal.title} | Saved Goal Page`)

        this.$store.commit('customgoal/setShareStatus', {
          shared: savedGoal.shared,
          shareLink: savedGoal.share_link,
        })

        await this.fetchPageContent({
          type: CONTENT_TYPES.GOAL,
          attribute: 'uuid',
          value: savedGoal.goal_uuid,
        })

        this.$sendEvent('Viewed Library Goal', {})
      } catch (err) {
        if (err.status_code === 403) {
          this.hasAccess = false
          setTitle('Saved Goal Page')
        }
        console.error('Error loading saved goal content page - loadPageContent', err)
      }
    },
    initializeEditableTextFromSaved() {
      this.editableText.goalText = this.initialSavedPage.grade_level_goal
      this.editableText.adaptedGoals = this.adaptedGoalsArray.map((goal) => {
        return {
          level: goal.level,
          text: goal.text,
        }
      })
      this.initializeCustomGoal({
        contentUuid: this.initialSavedPage.goal_uuid,
        primaryGoalText: this.initialSavedPage.goal_text,
        adaptedGoalsArray: this.adaptedGoalsArray,
      })
    },
    saveChanges() {
      this.saving = true
      this.justSaved = false
      saveGoalForStudent(this.initialSavedPage.uuid, this.savedGoalPayload)
        .then((resp) => {
          this.saving = false
          this.setJustSaved()
          this.initialSavedPage = resp
          this.initializeEditableTextFromSaved()
        })
        .catch((err) => {
          console.error('Error saving saved goal content page', err)
          this.saveError = err
          this.saving = false
        })

      this.$sendEvent('Clicked Save Changes', {
        uuid: this.uuid,
        type: CONTENT_TYPES.SAVED_GOAL,
      })
    },
    discardChanges() {
      if (confirm('Are you sure you want to discard any unsaved changes?')) {
        this.initializeEditableTextFromSaved()
      }
    },
    setJustSaved() {
      this.justSaved = true
      setTimeout(() => {
        this.justSaved = false
      }, 3000)
    },
    gradeGoalEdited() {
      this.handleGoalTextEdited({ level: 'grade', text: this.editableText.goalText })
    },
    adaptedGoalTextEdited(payload) {
      this.handleGoalTextEdited(payload)
    },
    onPrint() {
      this.trackPrint({
        contentType: CONTENT_TYPES.SAVED_GOAL,
        contentTitle: this.initialSavedPage.title,
        otherData: {
          goal_uuid: this.initialSavedPage.goal_uuid,
        },
      })
    },
    togglePrintView(flag) {
      if (flag) {
        this.$sendEvent('Clicked Print Saved Goal Page', {
          access: !this.actionPermissions.printSavedGoal.block,
          uuid: this.uuid,
        })
      }
      if (flag && this.actionPermissions.printSavedGoal.block) {
        this.showPrintBlocker = true
        return
      }
      this.showPrintablePage = flag
    },
  },
  eventContext() {
    return {
      goal_subject: this.pageContent.subject,
      goal_subject_domains: this.pageContent.subject_domains,
      goal_grades: this.pageContent.grades,
      goal_uuid: this.pageContent.uuid,
      goal_title: this.pageContent.title,
      goal_standards:
        this.pageContent.standard_set_refs &&
        this.pageContent.standard_set_refs.map((setRef) => setRef.standard_long_ref),
      goal_author_id: this.pageContent.author_id,
      goal_is_anchor: this.pageContent.super_page,
      goal_is_library: true,
      is_library: true,
      saved_goal_uuid: this.uuid,
      student_uuid: this.student.uuid,
    }
  },
  watch: {
    $route: function () {
      this.loadPageContent()
    },
  },
  created() {
    this.loadPageContent()
    this.fetchCurrentContentFolder({
      contentUuid: this.uuid,
      userUuid: this.userUuid,
      contentType: CONTENT_TYPES.SAVED_GOAL,
    })
  },
  beforeDestroy() {
    this.clearCurrentContentFolder()
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
@import '~@/mixins.less';
.label {
  padding: 0.3em 0.7em 0.4em;
  font-size: 85%;
}
.label-container {
  margin-bottom: 10px;
}
</style>
